import ky from 'ky'

import { objectToUrl } from './helpers'

/**
 * Default fetch for "get" methods
 * @param {string} url The url to fetch
 * @param {Object} params The query params
 * @param {import('ky').Options} options The request configuration
 * @param {Object} options.headers The headers to send
 * @param {string} [options.contentType='json'] The content type of the response (json or text)
 * @returns {Promise<Response>} The response data
 */
export async function getFetch(url, params = {}, options = {}) {
  if (!url) return

  const { headers, contentType = 'json', ...rest } = options || {}

  // Get query params from the options (query string i.e. ?key=val)
  const hasParams = Object.keys(params || {}).length > 0
  const queryParams = hasParams ? `?${objectToUrl(params)}` : ''

  try {
    const response = await ky.get(url, {
      searchParams: queryParams,
      headers,
      ...rest,
    })

    return contentType === 'json'
      ? await response.json()
      : await response.text()
  } catch (error) {
    throw error.response
  }
}

/**
 * Default fetch for "post" methods
 * @param {string} url The url to fetch
 * @param {Object} data The data to send
 * @param {import('ky').Options} options The request configuration
 * @param {Object} options.headers The headers to send
 * @returns {Promise<Response>} The response data
 */
export async function postFetch(url, data = {}, options = {}) {
  if (!url) return

  const { headers, ...rest } = options || {}

  try {
    return await ky.post(url, { json: data, headers, ...rest }).json()
  } catch (error) {
    throw error.response
  }
}

/**
 * Default fetch for "put" methods
 * @param {string} url The url to fetch
 * @param {Object} data The data to send
 * @param {import('ky').Options} options The request configuration
 * @param {Object} options.headers The headers to send
 * @param {'json'| 'formData'} [options.contentType='json'] The content type of the request (json or formData)
 * @returns {Promise<Response>} The response data
 */
export async function putFetch(url, data = {}, options = {}) {
  if (!url) return

  const { headers, contentType = 'json', ...rest } = options || {}

  const kyOptions = {
    headers,
    ...rest,
    timeout: 600000, // 600 seconds (10 minutes)
  }

  switch (contentType) {
    case 'formData': // Used in uploading files for example
      kyOptions.body = data
      break

    case 'json':
    default:
      kyOptions.json = data || {}
      break
  }

  // Note that onUploadProgress is not working with ky and s3.

  try {
    return await ky.put(url, kyOptions).json()
  } catch (error) {
    throw error.response
  }
}

/**
 * Default fetch for "patch" methods
 * @param {string} url The url to fetch
 * @param {Object} data The data to send
 * @param {import('ky').Options} options The request configuration
 * @param {Object} options.headers The headers to send
 * @param {string} [options.contentType='json'] The content type of the response (json or text)
 * @returns {Promise<Response>} The response data
 */
export async function patchFetch(url, data = {}, options = {}) {
  if (!url) return

  const { headers, contentType = 'json', ...rest } = options || {}

  const kyOptions = {
    headers,
    ...rest,
  }

  switch (contentType) {
    case 'formData': // Used in uploading files for example
      kyOptions.body = data
      break

    case 'json':
    default:
      kyOptions.json = data || {}
      break
  }

  try {
    return await ky.patch(url, kyOptions).json()
  } catch (error) {
    throw error.response
  }
}

const validFolders = ['fonts', 'zafir']

/**
 * Default fetch uploading files
 * @param {string} url The url to fetch
 * @param {Object} data The data to send
 * @param {Object} options The request configuration
 * @param {function} options.onProgress The function to call on progress
 * @param {function} options.onStart The function to call on start
 * @param {function} options.onComplete The function to call on complete
 * @param {boolean} options.multiple Indicates if multiple files can be uploaded
 * @param {'zafir'|'fonts'} options.folder The folder to upload the file to
 * @param {boolean} options.privateUpload Indicates if the file is private
 * @returns {Promise<Response>} The response data
 */
export async function fileUpload(
  url,
  data = {},
  options = {
    onProgress: undefined,
    onStart: undefined,
    onComplete: undefined,
    multiple: false,
    folder: '',
    privateUpload: false,
  }
) {
  if (!url) return

  const formData = new FormData()

  if (options?.multiple) {
    for (let i = 0; i < data.length; i++) {
      formData.append('files', data[i])
    }
  } else {
    formData.append('file', data)
  }

  // Add folder option to the formData if it's true
  if (options?.folder && validFolders.includes(options.folder)) {
    formData.append('folder', options.folder)
  }

  // Add the privateUpload option to the formData if it's true
  if (options?.privateUpload === true) {
    formData.append('privateUpload', true)
  }

  try {
    if (typeof options?.onStart === 'function') {
      options.onStart()
    }

    // Call the API with the formData
    const response = await ky
      .post(url, {
        body: formData,

        // TODO: The progress event is not working at the moment. A cryptic error is thrown: net::ERR_H2_OR_QUIC_REQUIRED

        // onUploadProgress: progress => {
        //   if (typeof options?.onProgress === 'function') {
        //     const { percent, transferredBytes, totalBytes } = progress
        //     options.onProgress(percent, transferredBytes, totalBytes)
        //   }
        // },
        timeout: 600000, // 600 seconds (10 minutes)
      })
      .json()

    // Call the onComplete function if it's a function
    if (typeof options?.onComplete === 'function') {
      options.onComplete(response)
    }

    return response
  } catch (error) {
    throw error.response
  }
}

/**
 * Default fetch for "patch" methods
 * @param {string} url The url to fetch
 * @param {import('ky').Options} options The request configuration
 * @param {Object} options.headers The headers to send
 * @returns {Promise<Response>} The response data
 */
export async function deleteFetch(url, options = {}) {
  if (!url) return

  const { headers, ...rest } = options || {}

  try {
    return await ky.delete(url, { headers, ...rest }).json()
  } catch (error) {
    throw error.response
  }
}
